import Vue from 'vue'

const OrderStatus = {
    CREATED: 1,
    PENDING_PAYMENT: 2,
    FAILED: 3,
    CANCELED: 4,
    PROCESSING: 5,
    COMPLETED: 6
}

const state = {
    picaServiceId: null,
    orders: [],
    currentOrder: null,
    orderItems: [],

    // Complete media data for prefetch approach
    orderItemsMedia: {},
    // Structure: {
    //   [order_item_id]: {
    //     allMediaIds: [],      // All available media IDs for this item
    //     selectedIds: [],     // Currently selected media IDs for operations
    //     media: [],           // Complete array of media objects
    //     loaded: false,       // Flag if we've loaded all media
    //     loading: false,      // Loading state
    //     error: null         // Error if fetch failed
    //   }
    // }

    filters: {
        status: `${OrderStatus.PROCESSING},${OrderStatus.COMPLETED}`, // Default: show all (PROCESSING + COMPLETED)
        order_number: '',
        created_at_range: null,
        page: 1,
        page_size: 100
    },
    loading: {
        orders: false,
        orderDetail: false
    },
    totalOrders: 0,
    totalPages: 0
}

// Helper to normalize order data
const normalizeOrder = (order) => {
    if (!order) return null
    return {
        ...order,
        // Extract order number from payment_data (no fallback for consistency)
        order_number: (order.payment_data &&
            order.payment_data.metadata &&
            order.payment_data.metadata.sapi &&
            order.payment_data.metadata.sapi.sale_response &&
            order.payment_data.metadata.sapi.sale_response.SalNo) || '-',
        // Extract transaction number from payment_data.metadata
        transaction_number: (order.payment_data &&
            order.payment_data.metadata &&
            order.payment_data.metadata.transaction_number) || '-',
        // Extract cabin, guest, and badge IDs from payment_data.customer
        cabin_id: (order.payment_data &&
            order.payment_data.customer &&
            order.payment_data.customer.cabin_id) || '-',
        guest_id: (order.payment_data &&
            order.payment_data.customer &&
            order.payment_data.customer.guest_id) || '-',
        badge_id: (order.payment_data &&
            order.payment_data.customer &&
            order.payment_data.customer.badge_id) || '-'
    }
}

export const getters = {
    picaServiceId: state => state.picaServiceId,
    orders: state => state.orders.map(normalizeOrder),
    currentOrder: state => {
        const order = normalizeOrder(state.currentOrder)
        if (!order) return null

        // Build bundles from order items
        const bundleGroups = {}
        const bundles = []

        state.orderItems.forEach(item => {
            if (item.bundle && item.bundle.id) {
                const bundleId = String(item.bundle.id)
                if (!bundleGroups[bundleId]) {
                    bundleGroups[bundleId] = {
                        ...item.bundle,
                        items: [],
                        total_price: 0,
                        discount_amount: 0,
                        unit_price: 0,
                        quantity: item.bundle_quantity
                    }
                    bundles.push(bundleGroups[bundleId])
                }
                bundleGroups[bundleId].items.push(item)
                bundleGroups[bundleId].total_price += parseFloat(item.total_price) || 0
                bundleGroups[bundleId].discount_amount += parseFloat(item.discount_amount) || 0
                bundleGroups[bundleId].unit_price += parseFloat(item.unit_price) || 0
            }
        })

        return {
            ...order,
            bundles
        }
    },
    currentOrderId: state => state.currentOrder?.id,
    orderItems: state => state.orderItems,
    getOrderItemById: state => orderItemId => {
        return state.orderItems.find(item => item.id === orderItemId)
    },

    filters: state => state.filters,
    loading: state => state.loading,
    totalOrders: state => state.totalOrders,
    totalPages: state => state.totalPages,

    // Media getters by order item (using new prefetch structure)
    getMediaForOrderItem: state => orderItemId => {
        const data = state.orderItemsMedia[orderItemId]
        if (!data) {
            return { media: [], pagination: null, selectedIds: [], loading: false }
        }
        return {
            media: data.media || [],
            pagination: null, // Not needed with prefetch
            selectedIds: data.selectedIds || [],
            loading: data.loading
        }
    },

    // Get raw order item media data (for edgePrints compatibility)
    getOrderItemMediaData: state => orderItemId => {
        return state.orderItemsMedia[orderItemId] || null
    },

    getSelectedMediaForOrderItem: state => orderItemId => {
        const data = state.orderItemsMedia[orderItemId]
        if (!data) return []
        return data.selectedIds || []
    },
    isMediaLoadingForOrderItem: state => orderItemId => {
        return state.orderItemsMedia[orderItemId]?.loading || false
    },

    // Get total count of selected media across all order items
    getTotalSelectedCount: state => {
        let total = 0
        Object.values(state.orderItemsMedia).forEach(item => {
            if (item.selectedIds) {
                total += item.selectedIds.length
            }
        })
        return total
    },

    // Get all selected media with their order context
    getAllSelectedMedia: (state, getters) => {
        const selected = []
        Object.entries(state.orderItemsMedia).forEach(([orderItemId, data]) => {
            if (data.selectedIds && data.selectedIds.length > 0) {
                // Find the order item
                const orderItem = state.orderItems.find(item => item.id === parseInt(orderItemId))
                if (orderItem) {
                    data.selectedIds.forEach(mediaId => {
                        selected.push({
                            mediaId,
                            orderItemId: parseInt(orderItemId),
                            orderItem,
                            order: state.currentOrder
                        })
                    })
                }
            }
        })
        return selected
    },

    // Group order items by bundle
    groupedOrderItems: state => {
        const grouped = []
        const bundleGroups = {}

        state.orderItems.forEach(item => {
            if (item.bundle && item.bundle.id) {
                // Group bundle items by bundle.id ONLY (not media_ids)
                const groupKey = String(item.bundle.id)

                if (!bundleGroups[groupKey]) {
                    bundleGroups[groupKey] = {
                        ...item,
                        isBundleGroup: true,
                        bundleItems: [],
                        // Aggregate price fields
                        total_price: 0,
                        discount_amount: 0,
                        unit_price: 0
                    }
                    grouped.push(bundleGroups[groupKey])
                }

                bundleGroups[groupKey].bundleItems.push(item)
                // Sum total_price from all items in the bundle
                bundleGroups[groupKey].total_price += parseFloat(item.total_price) || 0
                bundleGroups[groupKey].discount_amount += parseFloat(item.discount_amount) || 0
                bundleGroups[groupKey].unit_price += parseFloat(item.unit_price) || 0

                // Use bundle_quantity from the first item
                if (item.bundle_quantity !== undefined) {
                    bundleGroups[groupKey].quantity = item.bundle_quantity
                }
            } else {
                // Non-bundle items
                grouped.push(item)
            }
        })

        return grouped
    },

    isProcessingOrder: state => {
        return state.currentOrder?.status === OrderStatus.PROCESSING
    },

    isCompletedOrder: state => {
        return state.currentOrder?.status === OrderStatus.COMPLETED
    },

}

export const mutations = {
    setPicaServiceId(state, id) {
        state.picaServiceId = id
    },

    setOrders(state, orders) {
        state.orders = orders
    },

    setCurrentOrder(state, order) {
        state.currentOrder = order
        state.orderItems = order?.order_items || []
    },

    setFilters(state, filters) {
        state.filters = { ...state.filters, ...filters }
    },

    setLoading(state, { type, value }) {
        state.loading[type] = value
    },

    setTotalOrders(state, total) {
        state.totalOrders = total
    },

    setTotalPages(state, pages) {
        state.totalPages = pages
    },

    updateOrderStatus(state, status) {
        if (state.currentOrder) {
            state.currentOrder.status = status
        }
    },

    // New mutations for prefetch approach
    setOrderItemMediaLoading(state, { orderItemId, loading }) {
        if (!state.orderItemsMedia[orderItemId]) {
            Vue.set(state.orderItemsMedia, orderItemId, {
                allMediaIds: [],
                selectedIds: [],
                media: [],
                loaded: false,
                loading: false,
                error: null
            })
        }
        state.orderItemsMedia[orderItemId].loading = loading
    },

    setOrderItemMediaData(state, { orderItemId, media }) {
        if (!state.orderItemsMedia[orderItemId]) {
            Vue.set(state.orderItemsMedia, orderItemId, {
                allMediaIds: [],
                selectedIds: [],
                media: [],
                loaded: false,
                loading: false,
                error: null
            })
        }

        const itemData = state.orderItemsMedia[orderItemId]
        // Use Vue.set for reactivity
        Vue.set(itemData, 'media', media)
        Vue.set(itemData, 'allMediaIds', media.map(m => m.id))
        Vue.set(itemData, 'loaded', true)
        Vue.set(itemData, 'loading', false)
        Vue.set(itemData, 'error', null)
    },

    setOrderItemMediaError(state, { orderItemId, error }) {
        if (!state.orderItemsMedia[orderItemId]) {
            Vue.set(state.orderItemsMedia, orderItemId, {
                allMediaIds: [],
                selectedIds: [],
                media: [],
                loaded: false,
                loading: false,
                error: null
            })
        }
        state.orderItemsMedia[orderItemId].error = error
        state.orderItemsMedia[orderItemId].loading = false
    },

    clearAllOrderItemData(state) {
        state.orderItemsMedia = {}
    },

    setOrderItemMediaSelection(state, { orderItemId, selectedIds }) {
        if (state.orderItemsMedia[orderItemId]) {
            state.orderItemsMedia[orderItemId].selectedIds = selectedIds
        }
    }
}

export const actions = service => ({
    setPicaServiceId({ commit }, id) {
        commit('setPicaServiceId', id)
    },

    async fetchOrders({ commit, state }) {
        commit('setLoading', { type: 'orders', value: true })
        try {
            const params = {
                ...state.filters,
                page: state.filters.page,
                page_size: state.filters.page_size
            }

            // Remove empty values
            Object.keys(params).forEach(key => {
                if (params[key] === null || params[key] === '') {
                    delete params[key]
                }
            })

            const response = await service.fetchOrders(state.picaServiceId, params)
            commit('setOrders', response.results || response)
            commit('setTotalOrders', response.count || 0)
            commit('setTotalPages', Math.ceil((response.count || 0) / state.filters.page_size))
            return response
        } catch (error) {
            throw error
        } finally {
            commit('setLoading', { type: 'orders', value: false })
        }
    },

    async fetchOrderDetail({ commit }, orderId) {
        commit('setLoading', { type: 'orderDetail', value: true })
        try {
            const order = await service.fetchOrderDetail(orderId)
            commit('setCurrentOrder', order)
            return order
        } catch (error) {
            throw error
        } finally {
            commit('setLoading', { type: 'orderDetail', value: false })
        }
    },

    // Prefetch all media for all order items (both printable and digital)
    async prefetchAllOrderMedia({ commit, state }) {
        // Get all items that have media (printable or digital)
        const itemsWithMedia = state.orderItems.filter(item =>
            item.is_printable ||
            (item.media_ids && item.media_ids.length > 0) ||
            (item.gallery_ids && item.gallery_ids.length > 0)
        )

        if (itemsWithMedia.length === 0) {
            return { success: true, itemsLoaded: 0 }
        }

        // Load all media for each item with media in parallel
        const promises = itemsWithMedia.map(async (orderItem) => {
            const orderItemId = orderItem.id

            // Skip if already loaded
            if (state.orderItemsMedia[orderItemId]?.loaded) {
                return { orderItemId, success: true, cached: true }
            }

            commit('setOrderItemMediaLoading', { orderItemId, loading: true })

            try {
                const picaCode = orderItem.picacode || state.currentOrder?.picacode
                if (!picaCode) {
                    window.log('Order item without picacode - skipping:', {
                        orderItemId,
                        orderItem,
                        currentOrderPicacode: state.currentOrder?.picacode
                    })
                    return { orderItemId, success: true, skipped: true, reason: 'No picacode' }
                }

                // Build request params
                const params = { picacode: picaCode }

                // Add media_ids or gallery_ids if available
                if (orderItem.media_ids?.length > 0) {
                    params.media_ids = orderItem.media_ids
                } else if (orderItem.gallery_ids?.length > 0) {
                    params.gallery_ids = orderItem.gallery_ids
                } else if (orderItem.product?.gallery_ids?.length > 0) {
                    params.gallery_ids = orderItem.product.gallery_ids
                }

                // Fetch all pages
                let allMedia = []
                let page = 1
                let hasMore = true

                while (hasMore) {
                    const response = await service.fetchEdgeOrderMedia(
                        state.picaServiceId,
                        params,
                        {
                            page,
                            page_size: 100
                        }
                    )

                    allMedia = [...allMedia, ...(response.results || [])]

                    // Check if there are more pages
                    hasMore = response.pagination?.next || false
                    page++

                    // Safety check
                    if (page > 100) {
                        console.warn('Stopping after 100 pages')
                        break
                    }
                }

                // Filter out digital media - edgeOrders only handles printable media
                const filteredMedia = allMedia.filter(m => m.is_printable !== false)

                // Store the complete data
                commit('setOrderItemMediaData', {
                    orderItemId,
                    media: filteredMedia
                })

                return { orderItemId, success: true, mediaCount: allMedia.length }

            } catch (error) {
                console.error(`Failed to prefetch media for order item ${orderItemId}:`, error)
                commit('setOrderItemMediaError', { orderItemId, error: error.message })
                return { orderItemId, success: false, error: error.message }
            }
        })

        const results = await Promise.all(promises)

        const successCount = results.filter(r => r.success).length
        const failureCount = results.filter(r => !r.success).length

        return {
            success: failureCount === 0,
            itemsLoaded: successCount,
            itemsFailed: failureCount,
            results
        }
    },

    async downloadMedia({ state }, { mediaIds, picaCode, picaServiceId }) {
        try {
            const params = {
                picacode: picaCode,
                media_ids: mediaIds
            }

            const response = await service.downloadMedia(
                picaServiceId || state.picaServiceId,
                params
            )

            return { success: true }
        } catch (error) {
            throw error
        }
    },

    async getAllOrderMediaIds({ state }) {
        // Collect all media IDs from all order items
        const allMediaIds = []

        for (const orderItemId in state.orderItemsMedia) {
            const mediaData = state.orderItemsMedia[orderItemId]
            if (mediaData?.loaded && mediaData.allMediaIds) {
                allMediaIds.push(...mediaData.allMediaIds)
            }
        }

        return allMediaIds
    },

    async updateOrderStatus({ commit }, { orderId, status }) {
        try {
            const result = await service.updateOrderStatus(orderId, status)
            commit('updateOrderStatus', status)
            return result
        } catch (error) {
            throw error
        }
    },

    async exportOrdersCsv({ state }) {
        try {
            const params = {
                ...state.filters
            }
            // Remove empty values
            Object.keys(params).forEach(key => {
                if (params[key] === null || params[key] === '') {
                    delete params[key]
                }
            })
            return await service.exportOrdersCsv(state.picaServiceId, params)
        } catch (error) {
            throw error
        }
    },

    updateFilters({ commit }, filters) {
        commit('setFilters', filters)
    },

    clearCurrentOrder({ commit }) {
        commit('setCurrentOrder', null)
        commit('clearAllOrderItemData')
    },

    // Media selection action
    toggleMediaSelectionForOrderItem({ state, commit }, { orderItemId, mediaId }) {
        const data = state.orderItemsMedia[orderItemId]
        if (!data) return

        const selectedIds = data.selectedIds || []
        const index = selectedIds.indexOf(mediaId)

        if (index > -1) {
            // Deselect
            selectedIds.splice(index, 1)
        } else {
            // Select
            selectedIds.push(mediaId)
        }

        commit('setOrderItemMediaSelection', { orderItemId, selectedIds: [...selectedIds] })
    },

    selectAllMediaForOrderItem({ state, commit }, orderItemId) {
        const data = state.orderItemsMedia[orderItemId]
        if (!data || !data.allMediaIds) return

        commit('setOrderItemMediaSelection', {
            orderItemId,
            selectedIds: [...data.allMediaIds]
        })
    },

    deselectAllMediaForOrderItem({ commit }, orderItemId) {
        commit('setOrderItemMediaSelection', { orderItemId, selectedIds: [] })
    },

    // Clear all selections across all order items
    clearAllSelections({ state, commit }) {
        Object.keys(state.orderItemsMedia).forEach(orderItemId => {
            if (state.orderItemsMedia[orderItemId]?.selectedIds?.length > 0) {
                commit('setOrderItemMediaSelection', { orderItemId, selectedIds: [] })
            }
        })
    },

    async selectAllMediaWithFetch({ state, commit, dispatch }, { orderItemId, orderItem, picaCode }) {
        // Ensure all media is loaded
        const data = state.orderItemsMedia[orderItemId]

        if (!data || !data.loaded) {
            // Load all media first
            await dispatch('prefetchOrderItemMedia', { orderItem, picaCode })
        }

        // Select all media IDs
        const updatedData = state.orderItemsMedia[orderItemId]
        if (updatedData && updatedData.allMediaIds) {
            commit('setOrderItemMediaSelection', {
                orderItemId,
                selectedIds: [...updatedData.allMediaIds]
            })
        }
    },

    // Compatibility action for components still using lazy load
    // Uses prefetched data when available
    async fetchMediaForOrderItemId({ state, dispatch, commit }, { orderItemId, orderItem, picaCode, page = 1, perPage = 100 }) {
        // Check if we have prefetched data
        const prefetchData = state.orderItemsMedia[orderItemId]

        if (prefetchData?.loaded) {
            // Return prefetched data in the old format
            return {
                results: prefetchData.media,
                pagination: {
                    page: 1,
                    total: prefetchData.media.length,
                    next: null,
                    previous: null
                }
            }
        }

        // If no prefetch data, fetch it directly from the API
        if (orderItem && picaCode) {
            try {
                // Build request params with media_ids or gallery_ids
                const requestParams = { picacode: picaCode }

                // Add media_ids or gallery_ids if available
                if (orderItem.media_ids && orderItem.media_ids.length > 0) {
                    requestParams.media_ids = orderItem.media_ids
                } else if (orderItem.gallery_ids && orderItem.gallery_ids.length > 0) {
                    requestParams.gallery_ids = orderItem.gallery_ids
                }

                const response = await service.fetchEdgeOrderMedia(
                    state.picaServiceId,
                    requestParams,
                    {
                        page,
                        page_size: perPage
                    }
                )

                // Store in cache for future use
                if (response.results) {
                    commit('setOrderItemMediaData', {
                        orderItemId,
                        media: response.results
                    })
                }

                return response
            } catch (error) {
                console.error('Failed to fetch media:', error)
            }
        }

        // Return empty if nothing worked
        return {
            results: [],
            pagination: null
        }
    }
})

export default edgeOrdersService => ({
    namespaced: true,
    state,
    getters,
    mutations,
    actions: actions(edgeOrdersService)
})